import { Component, OnInit, OnDestroy, NgZone, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Activity, Settings, Box, LogOut, ChevronDown, Check, FileText, Ship, Fuel, Package, FileSignature, Anchor, CreditCard, Users, ShoppingCart, BarChart3 } from 'lucide-angular';
import { AuthService, PermissionService } from '@marina/auth';
import {
  CustomerService,
  VesselService,
  LaunchService,
  LeaseService,
  InvoiceService,
  PaymentsHubService,
  LaunchBoardHubService,
  VesselsHubService,
  ContractsHubService,
} from '@marina/api-client';
import { ContractStatus, MarinaInfo, PagedResponse, UserDto } from '@marina/models';
import { EMPTY, Observable, Subject, forkJoin, of, interval } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { APP_VERSION } from '../../../version';
import { getHomeUrlPathForCurrentMarina } from '../../../utils/post-auth-home.util';
import { SidebarBadgeRefreshService, SidebarBadgeScope } from '../../services/sidebar-badge-refresh.service';
import { LaunchDto } from '@marina/api-client';
import { countLaunchesInRequestsColumn } from '../../../features/launch-board/launch-requests-column.utils';

function emptyPaged<T>(): PagedResponse<T> {
  return { items: [], totalCount: 0, pageNumber: 1, pageSize: 1 };
}

const SIDEBAR_LEASE_ATTENTION_STATUSES = [
  'Draft',
  'AwaitingCustomerSignature',
  'AwaitingMarinaSignature',
  'AwaitingInitialPayment',
  'PendingTermination',
  'Expired',
] as const;

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  accessibleMarinas$: Observable<MarinaInfo[]>;
  isSystemAdmin$: Observable<boolean>;
  currentUser$: Observable<UserDto | null>;
  currentRoute = '';

  // Lucide icon components
  Activity = Activity;
  Settings = Settings;
  Box = Box;
  LogOut = LogOut;
  ChevronDown = ChevronDown;
  Check = Check;
  FileText = FileText;
  Ship = Ship;
  Fuel = Fuel;
  Package = Package;
  FileSignature = FileSignature;
  Anchor = Anchor;
  CreditCard = CreditCard;
  Users = Users;
  ShoppingCart = ShoppingCart;
  BarChart3 = BarChart3;

  // Version from version.ts
  version = `v${APP_VERSION}`;

  /** Count of Prospective clients – shown as a badge on the sidebar Clients item */
  prospectiveCount = 0;

  /** Count of vessels needing attention: New OR Expired OR Pending (deduped by vessel id). */
  vesselAttentionCount = 0;

  /** Count matching launch board "Requests" column: REQUESTED (incl. legacy PENDING), excluding Wash Rack. */
  requestedLaunchCount = 0;

  /** Count of storage contracts needing attention (excluding Contracted and Terminated) */
  storageContractsCount = 0;

  /** Draft + Issued + Overdue combined (single badge in sidebar). */
  billingAttentionCount = 0;

  private destroy$ = new Subject<void>();
  private readonly ngZone = inject(NgZone);
  private readonly paymentsHub = inject(PaymentsHubService);
  private readonly launchBoardHub = inject(LaunchBoardHubService);
  private readonly vesselsHub = inject(VesselsHubService);
  private readonly contractsHub = inject(ContractsHubService);
  /** Marina id for which this sidebar holds SignalR ref-count subscriptions */
  private sidebarMarinaHubId: string | null = null;
  private hubPaymentUnsub: (() => void) | null = null;
  private hubInvoiceUnsub: (() => void) | null = null;
  private hubRealtimeUnsubs: (() => void)[] = [];

  constructor(
    public authService: AuthService,
    private router: Router,
    private permissionService: PermissionService,
    private customerService: CustomerService,
    private vesselService: VesselService,
    private launchService: LaunchService,
    private leaseService: LeaseService,
    private invoiceService: InvoiceService,
    private sidebarBadgeRefresh: SidebarBadgeRefreshService
  ) {
    this.accessibleMarinas$ = this.authService.initData$.pipe(
      takeUntil(this.destroy$),
      map((data) => data?.accessibleMarinas || [])
    );

    this.isSystemAdmin$ = this.authService.initData$.pipe(
      takeUntil(this.destroy$),
      map((data) => data?.isSystemAdmin || false)
    );

    this.currentUser$ = this.authService.currentUser$.pipe(takeUntil(this.destroy$));

    // Track current route for active state
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
      });
  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;

    this.hubPaymentUnsub = this.paymentsHub.onPaymentStatusChanged((dto) => {
      const mid = this.authService.getCurrentMarinaId();
      if (mid && dto.marinaId && dto.marinaId.toLowerCase() === mid.toLowerCase()) {
        this.reloadInvoiceBadgeCounts(mid);
      }
    });
    this.hubInvoiceUnsub = this.paymentsHub.onInvoiceRosterChanged((dto) => {
      const mid = this.authService.getCurrentMarinaId();
      if (mid && dto.marinaId && dto.marinaId.toLowerCase() === mid.toLowerCase()) {
        this.reloadInvoiceBadgeCounts(mid);
      }
    });

    this.setupRealtimeBadgeHubHandlers();

    this.authService.currentMarinaId$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((marinaId) => {
        void this.syncMarinaRealtimeSubscription(marinaId);
      });

    // Badge counts are scoped to the current marina. switchMap cancels in-flight requests when
    // the marina changes so stale responses cannot overwrite counts for the newly selected marina.
    this.authService.currentMarinaId$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        switchMap((marinaId) => this.loadSidebarBadgeCounts$(marinaId))
      )
      .subscribe();

    this.sidebarBadgeRefresh.onChange$
      .pipe(
        takeUntil(this.destroy$),
        concatMap((scope) => {
          const marinaId = this.authService.getCurrentMarinaId();
          if (!marinaId) {
            return EMPTY;
          }
          return this.refreshBadgesForScope$(marinaId, scope).pipe(catchError(() => EMPTY));
        })
      )
      .subscribe();

    // Periodic refresh to catch in-page creates/updates (every 5 minutes).
    interval(300000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const marinaId = this.authService.getCurrentMarinaId();
        if (marinaId) {
          this.loadSidebarBadgeCounts$(marinaId).subscribe();
        }
      });
  }

  private setupRealtimeBadgeHubHandlers(): void {
    if (this.hubRealtimeUnsubs.length > 0) {
      return;
    }
    this.hubRealtimeUnsubs.push(
      this.launchBoardHub.onLaunchCreated((dto) => {
        this.onLaunchBoardRealtimeEvent(dto.marinaId);
      }),
      this.launchBoardHub.onLaunchUpdated((dto) => {
        this.onLaunchBoardRealtimeEvent(dto.marinaId);
      }),
      this.launchBoardHub.onLaunchRemoved((dto) => {
        this.onLaunchBoardRealtimeEvent(dto.marinaId);
      }),
      this.vesselsHub.onVesselCreated((dto) => {
        this.onVesselsRealtimeEvent(dto.marinaId);
      }),
      this.vesselsHub.onVesselUpdated((dto) => {
        this.onVesselsRealtimeEvent(dto.marinaId);
      }),
      this.vesselsHub.onVesselRemoved((dto) => {
        this.onVesselsRealtimeEvent(dto.marinaId);
      }),
      this.contractsHub.onContractCreated((dto) => {
        this.onContractsRealtimeEvent(dto.marinaId);
      }),
      this.contractsHub.onContractUpdated((dto) => {
        this.onContractsRealtimeEvent(dto.marinaId);
      }),
      this.contractsHub.onContractRemoved((dto) => {
        this.onContractsRealtimeEvent(dto.marinaId);
      })
    );
  }

  private onLaunchBoardRealtimeEvent(marinaId: string): void {
    if (!this.isCurrentMarinaEvent(marinaId)) {
      return;
    }
    this.ngZone.run(() => this.sidebarBadgeRefresh.requestRefresh('launch'));
  }

  private onVesselsRealtimeEvent(marinaId: string): void {
    if (!this.isCurrentMarinaEvent(marinaId)) {
      return;
    }
    this.ngZone.run(() => this.sidebarBadgeRefresh.requestRefresh('vessels'));
  }

  private onContractsRealtimeEvent(marinaId: string): void {
    if (!this.isCurrentMarinaEvent(marinaId)) {
      return;
    }
    this.ngZone.run(() => {
      this.sidebarBadgeRefresh.requestRefresh('contracts');
      this.sidebarBadgeRefresh.requestRefresh('vessels');
    });
  }

  private isCurrentMarinaEvent(marinaId: string): boolean {
    const mid = this.authService.getCurrentMarinaId();
    return !!mid && !!marinaId && marinaId.toLowerCase() === mid.toLowerCase();
  }

  private async syncMarinaRealtimeSubscription(marinaId: string | null): Promise<void> {
    if (this.sidebarMarinaHubId) {
      const previousMarinaId = this.sidebarMarinaHubId;
      await Promise.all([
        this.paymentsHub.unsubscribeFromMarina(previousMarinaId).catch(() => {}),
        this.launchBoardHub.unsubscribeFromLaunchBoard(previousMarinaId).catch(() => {}),
        this.vesselsHub.unsubscribeFromVessels(previousMarinaId).catch(() => {}),
        this.contractsHub.unsubscribeFromContracts(previousMarinaId).catch(() => {}),
      ]);
      this.sidebarMarinaHubId = null;
    }
    if (!marinaId) {
      return;
    }
    this.sidebarMarinaHubId = marinaId;
    try {
      await Promise.all([
        this.paymentsHub.connect(),
        this.launchBoardHub.connect(),
        this.vesselsHub.connect(),
        this.contractsHub.connect(),
      ]);
      // Per-hub subscribe: one denied hub must not fail the others (role gates differ).
      const results = await Promise.allSettled([
        this.paymentsHub.subscribeToMarina(marinaId),
        this.launchBoardHub.subscribeToLaunchBoard(marinaId),
        this.vesselsHub.subscribeToVessels(marinaId),
        this.contractsHub.subscribeToContracts(marinaId),
      ]);
      for (const result of results) {
        if (result.status === 'rejected') {
          console.warn('[Sidebar] Realtime hub subscribe to marina failed', result.reason);
        }
      }
    } catch (e) {
      console.warn('[Sidebar] Realtime hub connect to marina failed', e);
    }
  }

  /** Same scope as API InvoiceAccess — deck hands must not call invoice list or 403 triggers global /unauthorized redirect. */
  private canViewInvoiceSidebarBadges(marinaId: string): boolean {
    if (this.permissionService.isSystemAdmin()) {
      return true;
    }
    return this.permissionService.hasAnyMarinaRole(
      marinaId,
      'ADMIN',
      'DOCK_MASTER',
      'OFFICE_ADMIN',
      'SERVICE_MANAGER'
    );
  }

  /** Refresh invoice badges when SignalR reports billing-relevant changes (same filters as initial load). */
  private reloadInvoiceBadgeCounts(marinaId: string): void {
    if (!this.canViewInvoiceSidebarBadges(marinaId)) {
      return;
    }
    this.fetchInvoiceBadgeCounts$(marinaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ total }) => {
          this.ngZone.run(() => {
            this.billingAttentionCount = total;
          });
        },
        error: () => {},
      });
  }

  private fetchInvoiceBadgeCounts$(_marinaId: string): Observable<{ total: number }> {
    return forkJoin([
      this.invoiceService
        .getAllFiltered({ page: 1, pageSize: 1, status: 'Draft' })
        .pipe(catchError(() => of(emptyPaged()))),
      this.invoiceService
        .getAllFiltered({ page: 1, pageSize: 1, status: 'ISSUED' })
        .pipe(catchError(() => of(emptyPaged()))),
      this.invoiceService
        .getAllFiltered({ page: 1, pageSize: 1, status: 'OVERDUE' })
        .pipe(catchError(() => of(emptyPaged()))),
    ]).pipe(
      map(([draftResp, issuedResp, overdueResp]) => ({
        total:
          (draftResp.totalCount || 0) +
          (issuedResp.totalCount || 0) +
          (overdueResp.totalCount || 0),
      }))
    );
  }

  private refreshBadgesForScope$(marinaId: string, scope: SidebarBadgeScope): Observable<unknown> {
    switch (scope) {
      case 'all':
        return this.loadSidebarBadgeCounts$(marinaId);
      case 'launch':
        return this.refreshLaunchRequestedCount$(marinaId);
      case 'clients':
        return this.refreshProspectiveCount$();
      case 'vessels':
        return this.refreshVesselAttentionCount$();
      case 'contracts':
        return this.refreshStorageContractsCount$();
      case 'billing':
        return this.refreshBillingAttentionCount$(marinaId);
      default:
        return EMPTY;
    }
  }

  private refreshLaunchRequestedCount$(_marinaId: string): Observable<void> {
    return this.launchService.getDockBoardForCurrentMarina().pipe(
      catchError(() => of([] as LaunchDto[])),
      tap((items) => {
        this.requestedLaunchCount = countLaunchesInRequestsColumn(items ?? []);
      }),
      map(() => undefined)
    );
  }

  private refreshProspectiveCount$(): Observable<void> {
    return this.customerService
      .getAllFiltered({ pageNumber: 1, pageSize: 2000 })
      .pipe(
        catchError(() => of(emptyPaged())),
        tap((r) => {
          this.prospectiveCount = this.getProspectiveClientsCount(
            Array.isArray(r.items)
              ? (r.items as Array<{ clientStatus?: string | null }>)
              : []
          );
        }),
        map(() => undefined)
      );
  }

  private refreshVesselAttentionCount$(): Observable<void> {
    return this.vesselService.getAllFiltered({ pageNumber: 1, pageSize: 2000 }).pipe(
      catchError(() => of(emptyPaged())),
      tap((vesselsForAttention) => {
        const vesselItems = Array.isArray(vesselsForAttention.items)
          ? (vesselsForAttention.items as Array<{
              id: string;
              vesselStatus?: string | null;
              insurance?: { additionalInsuredVerified?: boolean; expiryDate?: string | null } | null;
              lease?: { status?: number; endDate?: string | null } | null;
            }>)
          : [];
        this.vesselAttentionCount = this.getVesselAttentionCount(vesselItems);
      }),
      map(() => undefined)
    );
  }

  private refreshStorageContractsCount$(): Observable<void> {
    const marinaId = this.authService.getCurrentMarinaId();
    if (!marinaId || !this.permissionService.canAccessLeaseContractApiForMarina(marinaId)) {
      this.storageContractsCount = 0;
      return of(undefined).pipe(map(() => undefined));
    }
    return forkJoin(
      SIDEBAR_LEASE_ATTENTION_STATUSES.map((status) =>
        this.leaseService
          .getAllFiltered({ pageNumber: 1, pageSize: 1, status })
          .pipe(catchError(() => of(emptyPaged())))
      )
    ).pipe(
      tap((leases) => {
        this.storageContractsCount = leases.reduce((sum, response) => sum + (response.totalCount || 0), 0);
      }),
      map(() => undefined)
    );
  }

  private refreshBillingAttentionCount$(marinaId: string): Observable<void> {
    if (!this.canViewInvoiceSidebarBadges(marinaId)) {
      this.billingAttentionCount = 0;
      return of(undefined).pipe(map(() => undefined));
    }
    return this.fetchInvoiceBadgeCounts$(marinaId).pipe(
      tap(({ total }) => {
        this.billingAttentionCount = total;
      }),
      map(() => undefined)
    );
  }

  private getVesselAttentionCount(
    vessels: Array<{
      id: string;
      vesselStatus?: string | null;
      insurance?: { additionalInsuredVerified?: boolean; expiryDate?: string | null } | null;
      lease?: { status?: number; endDate?: string | null } | null;
    }>
  ): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const unique = new Set<string>();

    for (const vessel of vessels) {
      if (this.vesselNeedsAttention(vessel, today)) {
        unique.add(vessel.id);
      }
    }

    return unique.size;
  }

  /**
   * Matches Vessels list tabs: New (no active contract), Expired insurance, Pending verification.
   * Boat Club vessels use system {@link vesselStatus} (Contracted without a storage lease).
   */
  private vesselNeedsAttention(
    vessel: {
      vesselStatus?: string | null;
      insurance?: { additionalInsuredVerified?: boolean; expiryDate?: string | null } | null;
      lease?: { status?: number; endDate?: string | null } | null;
    },
    today: Date
  ): boolean {
    const insurance = vessel.insurance;
    const hasInsurance = !!insurance;
    const insuranceExpired =
      !!insurance?.expiryDate &&
      new Date(`${String(insurance.expiryDate).substring(0, 10)}T00:00:00`).getTime() < today.getTime();
    const insurancePending = hasInsurance && !insuranceExpired && !insurance?.additionalInsuredVerified;
    const insuranceAttention = insuranceExpired || insurancePending;

    const statusNorm = (vessel.vesselStatus || '').trim().toLowerCase();
    if (statusNorm === 'contracted') {
      return insuranceAttention;
    }
    if (statusNorm === 'terminated') {
      return insuranceAttention;
    }

    const lease = vessel.lease;
    const hasContract = !!lease;
    const leaseStatus = lease?.status;
    const leaseEnded =
      !!lease?.endDate &&
      new Date(`${String(lease.endDate).substring(0, 10)}T00:00:00`).getTime() < today.getTime();
    const terminated = leaseStatus === ContractStatus.Terminated;
    const newVessel = !hasContract || terminated || leaseEnded;

    return newVessel || insuranceAttention;
  }

  private getProspectiveClientsCount(
    clients: Array<{ clientStatus?: string | null }>
  ): number {
    return clients.filter((c) => (c.clientStatus || '').toLowerCase() === 'prospective').length;
  }

  private loadSidebarBadgeCounts$(marinaId: string | null): Observable<unknown> {
    this.prospectiveCount = 0;
    this.vesselAttentionCount = 0;
    this.requestedLaunchCount = 0;
    this.storageContractsCount = 0;
    this.billingAttentionCount = 0;

    if (!marinaId) {
      return EMPTY;
    }

    return forkJoin({
      prospective: this.customerService
        .getAllFiltered({ pageNumber: 1, pageSize: 2000 })
        .pipe(catchError(() => of(emptyPaged()))),
      vesselsForAttention: this.vesselService
        .getAllFiltered({ pageNumber: 1, pageSize: 2000 })
        .pipe(catchError(() => of(emptyPaged()))),
      dockBoardLaunches: this.launchService
        .getDockBoardForCurrentMarina()
        .pipe(catchError(() => of([] as LaunchDto[]))),
      leases: this.permissionService.canAccessLeaseContractApiForMarina(marinaId)
        ? forkJoin(
            SIDEBAR_LEASE_ATTENTION_STATUSES.map((status) =>
              this.leaseService
                .getAllFiltered({ pageNumber: 1, pageSize: 1, status })
                .pipe(catchError(() => of(emptyPaged())))
            )
          )
        : of(SIDEBAR_LEASE_ATTENTION_STATUSES.map(() => emptyPaged())),
      invoices: this.canViewInvoiceSidebarBadges(marinaId)
        ? this.fetchInvoiceBadgeCounts$(marinaId)
        : of({ total: 0 }),
    }).pipe(
      tap(({ prospective, vesselsForAttention, dockBoardLaunches, leases, invoices }) => {
        const vesselItems = Array.isArray(vesselsForAttention.items)
          ? (vesselsForAttention.items as Array<{
              id: string;
              vesselStatus?: string | null;
              insurance?: { additionalInsuredVerified?: boolean; expiryDate?: string | null } | null;
              lease?: { status?: number; endDate?: string | null } | null;
            }>)
          : [];
        const customerItems = Array.isArray(prospective.items)
          ? (prospective.items as Array<{ clientStatus?: string | null }>)
          : [];
        this.prospectiveCount = this.getProspectiveClientsCount(customerItems);
        this.vesselAttentionCount = this.getVesselAttentionCount(vesselItems);
        this.requestedLaunchCount = countLaunchesInRequestsColumn(dockBoardLaunches ?? []);
        this.storageContractsCount = leases.reduce(
          (sum, response) => sum + (response.totalCount || 0),
          0
        );
        this.billingAttentionCount = invoices.total;
      })
    );
  }

  ngOnDestroy(): void {
    if (this.hubPaymentUnsub) {
      this.hubPaymentUnsub();
      this.hubPaymentUnsub = null;
    }
    if (this.hubInvoiceUnsub) {
      this.hubInvoiceUnsub();
      this.hubInvoiceUnsub = null;
    }
    this.hubRealtimeUnsubs.forEach((unsub) => unsub());
    this.hubRealtimeUnsubs = [];
    if (this.sidebarMarinaHubId) {
      const marinaId = this.sidebarMarinaHubId;
      Promise.all([
        this.paymentsHub.unsubscribeFromMarina(marinaId).catch(() => {}),
        this.launchBoardHub.unsubscribeFromLaunchBoard(marinaId).catch(() => {}),
        this.vesselsHub.unsubscribeFromVessels(marinaId).catch(() => {}),
        this.contractsHub.unsubscribeFromContracts(marinaId).catch(() => {}),
      ]).catch(() => {});
      this.sidebarMarinaHubId = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToMarina(marinaId: string): void {
    this.authService.setCurrentMarina(marinaId);
    void this.router.navigateByUrl(getHomeUrlPathForCurrentMarina(this.authService));
  }

  trackByMarinaId(index: number, marina: MarinaInfo): string {
    return marina.id;
  }

  getUserInitials(): string {
    const user = this.authService.currentUserValue;
    const displayName = user ? (user.name || [user.firstName, user.lastName].filter(Boolean).join(' ').trim()) : '';
    if (!displayName) {
      return 'U';
    }
    const names = displayName.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  }

  getRoleDisplayName(): string {
    const user = this.authService.currentUserValue;
    if (!user || user.staffRole === null || user.staffRole === undefined) {
      return '';
    }
    // Map staff role enum to display name
    const roleMap: { [key: number]: string } = {
      0: 'ADMIN',
      1: 'DOCK MASTER',
      2: 'OFFICE ADMIN',
      3: 'SERVICE MANAGER',
      4: 'SERVICE TECH',
      5: 'DECK HAND',
    };
    return roleMap[user.staffRole] || '';
  }

  getCurrentMarinaName(): string {
    const currentMarinaId = this.authService.getCurrentMarinaId();
    const initData = this.authService.initDataValue;
    if (!initData || !currentMarinaId) {
      const marinas = this.authService.getAccessibleMarinas();
      if (marinas.length > 0) {
        // Auto-select first marina if none selected
        this.authService.setCurrentMarina(marinas[0].id);
        return marinas[0].name;
      }
      return 'No Marina';
    }
    const marina = initData.accessibleMarinas.find((m) => m.id === currentMarinaId);
    return marina?.name || 'Select Marina';
  }

  isRouteActive(route: string): boolean {
    return this.currentRoute === route || this.currentRoute.startsWith(route + '/');
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        // Redirect to login page
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        // Even if logout fails, redirect to login (auth state is already cleared)
        this.router.navigate(['/auth/login']);
      },
    });
  }

  /**
   * Check if user has access to a route based on their role
   * Based on ROLE_PERMISSIONS from mock project:
   * - ADMIN: all
   * - DOCK_MASTER: dashboard, kanban, fleet, racks, fuel, issues, logs
   * - OFFICE_ADMIN: dashboard, fleet, contracts, billing, pos, inventory, settings
   * - SERVICE_MANAGER: dashboard, fleet (no contracts, audit logs, or marina settings)
   * - SERVICE_TECH: service-board, issues, parts
   * - DECK_HAND: launch board + POS & Shop
   */
  canAccessRoute(route: string): boolean {
    // System settings is only for System admin
    if (route === '/system-settings') {
      return this.permissionService.isSystemAdmin();
    }

    // Marina settings is for Marina users AND System admins (when they have a marina selected)
    if (route === '/marina-settings') {
      // System admins can access Marina Settings when they have a marina selected
      if (this.permissionService.isSystemAdmin()) {
        const currentMarinaId = this.authService.getCurrentMarinaId();
        return currentMarinaId !== null; // System admin needs a marina selected
      }
      // Marina leadership and operations (current marina only)
      const currentMarinaId = this.authService.getCurrentMarinaId();
      if (!currentMarinaId) {
        return false;
      }
      return (
        this.permissionService.hasMarinaRole(currentMarinaId, 'ADMIN') ||
        this.permissionService.hasMarinaRole(currentMarinaId, 'OFFICE_ADMIN') ||
        this.permissionService.hasMarinaRole(currentMarinaId, 'DOCK_MASTER')
      );
    }

    // System admin can access everything except system-settings and marina-settings (handled above)
    if (this.permissionService.isSystemAdmin()) {
      return true;
    }

    const currentMarinaId = this.authService.getCurrentMarinaId();
    if (!currentMarinaId) {
      return false;
    }

    // Check if user has ADMIN role (can access all)
    if (this.permissionService.hasMarinaRole(currentMarinaId, 'ADMIN')) {
      return true;
    }

    const routeRolesMap: { [key: string]: string[] } = {
      '/dashboard': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER', 'SERVICE_TECH'],
      '/marina-settings': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN'],
      '/system-settings': [], // System settings - only accessible to System admin (checked separately)
      '/dry-storage': ['ADMIN', 'DOCK_MASTER'],
      '/fleet-management': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'],
      '/vessels': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'],
      '/clients': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'],
      '/lease-management': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN'],
      '/billing': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'],
      '/reports': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER'],
      '/pos': ['ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN', 'SERVICE_MANAGER', 'DECK_HAND'],
      '/fuel-management': ['ADMIN', 'DOCK_MASTER'],
      '/inventory': [
        'ADMIN',
        'DOCK_MASTER',
        'OFFICE_ADMIN',
        'SERVICE_MANAGER',
        'SERVICE_TECH',
      ],
      '/audit-logs': ['ADMIN', 'DOCK_MASTER'],
      '/launch-board': ['ADMIN', 'DOCK_MASTER', 'DECK_HAND'],
    };

    const requiredRoles = routeRolesMap[route];
    if (requiredRoles === undefined) {
      // Unknown route, allow access (fallback)
      return true;
    }

    // Empty array means accessible to all authenticated users
    if (requiredRoles.length === 0) {
      return true;
    }

    // Check if user has any of the required roles in the current marina
    return this.permissionService.hasAnyMarinaRole(currentMarinaId, ...requiredRoles);
  }
}
